import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

import { differenceBy } from 'lodash-es';

import { formatISODate } from '../../helpers/formatISODate';
import {
  ACCOMMODATION_PARAM,
  ACCOMMODATION_VALUE,
  useAccommodationFilter,
} from '../../hooks/useAccommodationFilter';
import useBreakpoints from '../../hooks/useBreakpoints';
import { END_DATE_PARAM, START_DATE_PARAM, useDatesFilter } from '../../hooks/useDatesFilter';
import useTranslate from '../../hooks/useTranslate';
import getGolfCourseUrl from '../../services/apis/golfCourses/getGolfCourseUrl';
import { MappedGolfCourseType } from '../../services/apis/golfCourses/types';
import { sentryMessage } from '../../services/sentry';
import ScrollSnapSlider from '../ScrollSnapSlider/ScrollSnapSlider';
import TeeTimesAvailability from '../TeeTimesAvailability/TeeTimesAvailability';
import GolfCourse from './GolfCourse';

type GolfCoursesProps = {
  defaultExpandedGolfCourses?: MappedGolfCourseType[];
  golfCourses: MappedGolfCourseType[];
  selectedDate: string;
  topRatedGolfCourses?: MappedGolfCourseType[];
  topBookedGolfCourses?: MappedGolfCourseType[];
};

const GolfCourses: FC<GolfCoursesProps> = props => {
  const {
    golfCourses,
    selectedDate,
    defaultExpandedGolfCourses = [],
    topRatedGolfCourses = [],
    topBookedGolfCourses = [],
  } = props;
  const navigate = useNavigate();
  const { activeLanguage } = useTranslate();
  const [isAccommodationFilter] = useAccommodationFilter();
  const { startDate, endDate } = useDatesFilter();

  const [expandedGolfCourses, setExpandedGolfCourses] = useState<MappedGolfCourseType[]>(
    defaultExpandedGolfCourses
  );

  const [lastExpandedGolfCourseId, setLastExpandedGolfCourseId] =
    useState<MappedGolfCourseType['id']>();
  const collapsedGolfCourses = useMemo(
    () => differenceBy(golfCourses, expandedGolfCourses, golfCourse => golfCourse.golfCourse.id),
    [golfCourses, expandedGolfCourses]
  );
  const location = useLocation();

  const isBreakPoints = useBreakpoints();

  const handleShowTeeTimesClick = (golfCourse: MappedGolfCourseType) => {
    setExpandedGolfCourses(items => {
      // expand last item automatically
      if (collapsedGolfCourses.length === 2) {
        return [...items, ...collapsedGolfCourses];
      }

      return [...items, golfCourse];
    });
    setLastExpandedGolfCourseId(golfCourse.id);
  };

  const handleShowAvailabilityClick = (golfCourse: MappedGolfCourseType) => {
    const searchParams = new URLSearchParams();

    searchParams.set(START_DATE_PARAM, formatISODate(startDate));
    if (endDate) {
      searchParams.set(END_DATE_PARAM, formatISODate(endDate));
    }

    searchParams.set(ACCOMMODATION_PARAM, ACCOMMODATION_VALUE);

    navigate({
      pathname: `/${activeLanguage.code}/booking/${getGolfCourseUrl(golfCourse)}`,
      search: `?${searchParams.toString()}`,
    });
  };

  useEffect(() => {
    setExpandedGolfCourses([]);
  }, [location]);

  useEffect(() => {
    try {
      document.body
        .querySelector(`#golf-course-${lastExpandedGolfCourseId}`)
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } catch (err) {
      sentryMessage(
        'Automatically scroll to view expanded golf course, using scrollIntoView method with arguments',
        { err: String(err) }
      );
      document.body.querySelector(`#golf-course-${lastExpandedGolfCourseId}`)?.scrollIntoView();
    }
  }, [lastExpandedGolfCourseId]);

  return (
    <div className="d-flex flex-column gap-4">
      {expandedGolfCourses.map((golfCourse, id) => (
        <Row
          key={golfCourse.id}
          id={`golf-course-${golfCourse.id}`}
          style={{ scrollMarginTop: '165px' }}
        >
          <Col xs={12} lg={5}>
            <GolfCourse
              key={golfCourse.id}
              golfCourse={golfCourse}
              topRated={topRatedGolfCourses.some(
                ({ golfCourse: { id } }) => id === golfCourse.golfCourse.id
              )}
              topBooked={topBookedGolfCourses.some(
                ({ golfCourse: { id } }) => id === golfCourse.golfCourse.id
              )}
              discount={!!golfCourse.golfCourse.campaignPercentage}
              discountValue={golfCourse.golfCourse.campaignPercentage}
            />
          </Col>
          <Col xs={12} lg={7}>
            <TeeTimesAvailability
              golfCourse={golfCourse}
              selectedDate={selectedDate}
              shrinkDatesTable
            />
          </Col>
        </Row>
      ))}
      {!!collapsedGolfCourses.length && (
        <ScrollSnapSlider key={golfCourses.map(golfCourse => golfCourse.golfCourse.id).join('-')}>
          {collapsedGolfCourses.map(golfCourse => (
            <div
              key={golfCourse.id}
              className="d-flex flex-column"
              style={
                isBreakPoints.md
                  ? { minWidth: 500, maxWidth: 600 }
                  : { minWidth: 300, maxWidth: 350 }
              }
            >
              <GolfCourse
                golfCourse={golfCourse}
                onShowMoreClick={() =>
                  isAccommodationFilter
                    ? handleShowAvailabilityClick(golfCourse)
                    : handleShowTeeTimesClick(golfCourse)
                }
                topRated={topRatedGolfCourses.some(
                  ({ golfCourse: { id } }) => id === golfCourse.golfCourse.id
                )}
                topBooked={topBookedGolfCourses.some(
                  ({ golfCourse: { id } }) => id === golfCourse.golfCourse.id
                )}
                discount={!!golfCourse.golfCourse.campaignPercentage}
                discountValue={golfCourse.golfCourse.campaignPercentage}
              />
            </div>
          ))}
        </ScrollSnapSlider>
      )}
    </div>
  );
};

export default GolfCourses;
